import { useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import ReactECharts from "echarts-for-react";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { MultiSelectV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/MultiSelectV2";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useConfig } from "@/hooks/useConfig";
import { useMROIStore } from "@/pages/channel-roi/store/useMROIStore";
import { AggregatedData, ComparisonNodeData } from "@/pages/channel-roi/types";
import Palette from "@/Utils/Colors/palette";
import { Layer } from "@/Utils/hierarchy";

import { bubbleChartOptions } from "./bubbleChartOptions";
import { EmptyState } from "./EmptyState";
import Legend from "./Legend";

export type ChannelEffectivenessData = {
  channel: string;
  roas: number;
  contribution: number;
  spend: number;
  color: string;
};

interface Legend {
  name: string;
  color: string;
  isActive: boolean;
}

type BubbleChartProps = {
  nodes: AggregatedData[];
  targetNodeUUID: string;
  comparisonNodeData?: ComparisonNodeData[];
  nodeLayer: Layer;
  palette: Palette;
  height?: string;
  maxHeight?: string;
};

const getNodeName = (node: ComparisonNodeData, nodeLayer: Layer) => {
  switch (nodeLayer) {
    case Layer.Brand:
      return node.brand;
    case Layer.Category:
      return node.category;
    case Layer.Product:
      return node.product;
    default:
      return "";
  }
};

const BubbleChart = ({
  nodes,
  targetNodeUUID,
  comparisonNodeData,
  nodeLayer,
  palette,
  height = "80vh",
  maxHeight = "700px",
}: BubbleChartProps) => {
  const { config } = useConfig();
  const { nodes: nodeTreeNodes } = useInfo();
  const { compareNodeUUIDs } = useMROIStore((state) => state.filters);
  const { setCompareNodeUUIDs } = useMROIStore();
  const { formatCurrency } = config.currency;

  const { value: isBubbleChartComparisonEnabled } = useFeatureGate(
    featureFlag.mroiBubbleChartComparison
  );

  const theme = useTheme();

  const nodesInSameLayer = nodeTreeNodes?.getNodesInSameLayerAndParent(targetNodeUUID, true);

  const nodeOptions =
    nodesInSameLayer?.map((node) => ({
      label: node.warchestNode.name,
      value: node.node.uuid,
    })) ?? [];

  const handleNodeChange = (value: string[]) => {
    setCompareNodeUUIDs(value);
  };

  const [legends, setLegends] = useState<Legend[]>([]);

  useEffect(() => {
    const uniqueChannels = Array.from(
      new Set([
        ...nodes.map((node) => node.channel),
        ...(comparisonNodeData?.map((node) => node.channel) ?? []),
      ])
    );

    setLegends(
      uniqueChannels.map((channel) => ({
        name: channel,
        color: palette.chooseColorMemo(channel).color,
        isActive: true,
      }))
    );
  }, [nodes, comparisonNodeData]);

  const handleLegendClick = (name: string) => {
    const legend = legends.find((legend) => legend.name === name);
    if (legend) {
      legend.isActive = !legend.isActive;
      setLegends([...legends]);
    }
  };

  const filteredNodes = nodes.filter(
    (node) => legends.find((legend) => legend.name === node.channel)?.isActive
  );

  const filteredComparisonNodes =
    comparisonNodeData?.filter(
      (node) => legends.find((legend) => legend.name === node.channel)?.isActive
    ) ?? [];

  const filteredComparisonNodesWithName = filteredComparisonNodes.map((node) => ({
    ...node,
    nodeName: getNodeName(node, nodeLayer),
    isComparisonNode: true,
  }));

  const targetNodeName = nodeTreeNodes?.getNode(targetNodeUUID)?.warchestNode.name || "";
  const options = bubbleChartOptions(
    filteredNodes,
    theme,
    palette,
    formatCurrency,
    targetNodeName,
    filteredComparisonNodesWithName
  );

  if (isEmpty(nodes)) {
    return (
      <Stack height="50vh" maxHeight={maxHeight} direction="column">
        <EmptyState text="No data available. Please change your filters." />
      </Stack>
    );
  }

  if (isEmpty(filteredNodes)) {
    return (
      <Stack gap={2} height={height} maxHeight={maxHeight} direction="column">
        <EmptyState text="No channels selected. Select a channel from the legend below to populate the chart." />
        <Legend legends={legends} handleClick={handleLegendClick} />
      </Stack>
    );
  }

  return (
    <Stack gap={2} height={height} maxHeight={maxHeight} direction="column">
      <Stack justifyContent="end" mt={1} direction="row" alignItems="center" gap={2}>
        {isBubbleChartComparisonEnabled && nodeOptions && nodeOptions.length > 0 && (
          <MultiSelectV2
            width={360}
            multiple
            disableClearable
            id="compare"
            label="Compare"
            placeholder="None"
            options={nodeOptions}
            value={compareNodeUUIDs}
            onChange={handleNodeChange}
          />
        )}
        <Typography variant="body1" fontSize={14}>
          Bubble Size = Spend
        </Typography>
      </Stack>
      <Box flexGrow={1} minHeight={0}>
        <ReactECharts
          key={JSON.stringify(`${filteredNodes}-${filteredComparisonNodes}`)}
          option={options}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "svg" }}
        />
      </Box>
      <Box mt={2}>
        <Legend legends={legends} handleClick={handleLegendClick} />
      </Box>
    </Stack>
  );
};

export default BubbleChart;
